import {
  BadgeDollarSign,
  PackagePlus,
  BarChart3,
  Sparkles,
  ToggleRight,
  Bug,
  Building2,
  CalendarClock,
  Clock3,
  CreditCard,
  Download,
  FileText,
  KeyRound,
  Layers,
  Mail,
  MessageCircle,
  Receipt,
  Settings,
  SlidersHorizontal,
  UserPlus,
  Users,
  Zap,
  type LucideIcon
} from 'lucide-react';

export interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
  /**
   * The single declaration of "admins only". It hides the item from the sidebar
   * and the command palette for non-admins, AND puts the AdminOnly wall in
   * front of the route itself (see AppShell) for anyone who types the URL. A
   * new admin-only screen is declared here and nowhere else — pages must not
   * gate themselves, or the two would drift the way they already had.
   */
  adminOnly?: boolean;
  /**
   * Hidden unless the flag is on: 'originalTimesheets' when the team enabled
   * them, 'devOnly' on a developer's machine (see useFeatureFlags). A devOnly
   * route also needs the <DevOnly> wall in routes.tsx for anyone typing the
   * URL; the nav item hides it, the wall refuses it.
   */
  flag?: 'originalTimesheets' | 'devOnly';
  /**
   * A feature flag from /admin/flags (apps/server lib/feature-flags.ts). The
   * item shows only when the server says the flag is on for this person; the
   * route needs the matching <FeatureGate> in routes.tsx.
   */
  feature?: string;
  /**
   * Billable's operators only (session.isPlatformAdmin, i.e. Rhett), not a
   * firm's admins. The routes carry the <PlatformAdminOnly> wall in routes.tsx;
   * the server 404s /api/admin for everyone else regardless.
   */
  platformAdmin?: boolean;
  keywords?: string[];
  /** Active only on this exact path, not below it (Connect AI at /ai vs /ai/assistant). */
  end?: boolean;
}

export interface NavGroup {
  label: string;
  items: NavItem[];
}

/**
 * Nav is defined once and drives both the sidebar and the command palette. In
 * v1 those were two separate hand-maintained lists that had already drifted —
 * the palette linked to /balances (absent from the sidebar) and omitted Reports
 * and Invoice Settings.
 */
export function navGroups(nouns: { clients: string; payers: string }): NavGroup[] {
  return [
    {
      label: 'Work',
      items: [
        { label: 'Timesheet', to: '/timesheet', icon: CalendarClock, keywords: ['daycard', 'hours', 'time'] },
        { label: 'Invoices', to: '/invoices', icon: FileText, keywords: ['bill', 'invoice'] },
        {
          label: 'Quick Invoice',
          to: '/quick-invoice',
          icon: Zap,
          adminOnly: true,
          keywords: ['batch', 'tentative']
        },
        { label: 'Credits & Fees', to: '/credits-and-fees', icon: BadgeDollarSign, keywords: ['discount', 'expense'] },
        { label: 'Reports', to: '/reports', icon: BarChart3, adminOnly: true, keywords: ['analytics', 'revenue'] }
      ]
    },
    {
      label: 'Manage',
      items: [
        { label: `Manage ${nouns.clients}`, to: '/manage/clients', icon: Users },
        { label: 'Manage Activities', to: '/manage/activities', icon: Layers },
        { label: `Manage ${nouns.payers}`, to: '/manage/billables', icon: Building2 },
        { label: 'Credit & Fee Templates', to: '/manage/credit-fee-templates', icon: Receipt },
        {
          label: 'Import & integrations',
          to: '/manage/import',
          icon: Download,
          keywords: ['import', 'harvest', 'csv', 'sheets', 'toggl', 'clockify', 'integrations']
        },
        { label: 'Emails', to: '/emails', icon: Mail, keywords: ['sent', 'history'] }
      ]
    },
    {
      label: 'Account',
      items: [
        { label: 'Personal Hours', to: '/personal-hours', icon: Clock3, keywords: ['payroll', 'pay period'] },
        { label: 'Original Timesheets', to: '/original-timesheets', icon: CalendarClock, flag: 'originalTimesheets' },
        { label: 'Manage Team', to: '/team', icon: Users, keywords: ['members', 'invite'] },
        { label: 'Subscription', to: '/subscription', icon: CreditCard, keywords: ['billing', 'plan', 'upgrade'] },
        { label: 'Invoice Settings', to: '/invoice-settings', icon: SlidersHorizontal },
        { label: 'API & CLI', to: '/developers', icon: KeyRound, flag: 'devOnly', keywords: ['api', 'key', 'cli', 'developer'] },
        { label: 'Settings', to: '/settings', icon: Settings, keywords: ['profile', 'company', 'timezone'] }
      ]
    },
    {
      label: 'AI',
      items: [
        {
          label: 'Assistant',
          to: '/ai/assistant',
          icon: MessageCircle,
          feature: 'assistant',
          keywords: ['chat', 'ask', 'assistant', 'credits']
        },
        {
          label: 'Connect AI',
          to: '/ai',
          end: true,
          icon: Sparkles,
          feature: 'ai',
          keywords: ['chatgpt', 'claude', 'mcp', 'agent']
        }
      ]
    },
    {
      label: 'Admin',
      items: [
        { label: 'Bug reports', to: '/admin/bug-reports', icon: Bug, platformAdmin: true, keywords: ['bugs', 'admin', 'feedback'] },
        { label: 'Feature flags', to: '/admin/flags', icon: ToggleRight, platformAdmin: true, keywords: ['flags', 'release', 'rollout'] },
        { label: 'Sign-ups', to: '/admin/signups', icon: UserPlus, platformAdmin: true, keywords: ['signups', 'survey', 'admin', 'onboarding'] },
        { label: 'Import requests', to: '/admin/import-requests', icon: PackagePlus, platformAdmin: true, keywords: ['imports', 'platforms', 'integrations', 'admin'] }
      ]
    }
  ];
}

/**
 * Every `to` marked `adminOnly` above. Nouns don't affect which items are
 * admin-only, so placeholders are fine here — this only reads paths.
 */
const ADMIN_ONLY_PATHS = navGroups({ clients: '', payers: '' })
  .flatMap((group) => group.items)
  .filter((item) => item.adminOnly)
  .map((item) => item.to);

/**
 * True for an admin-only nav path and anything nested under it, so
 * /reports/revenue is gated by the /reports declaration rather than needing one
 * of its own. Matching stops at a segment boundary, so /reports-archive would
 * not be caught by /reports.
 */
export function isAdminOnlyPath(pathname: string): boolean {
  return ADMIN_ONLY_PATHS.some(
    (path) => pathname === path || pathname.startsWith(`${path}/`)
  );
}

/**
 * The items a given user should actually see. The sidebar and the command
 * palette both go through this so neither can advertise a route its owner
 * would be walled out of.
 */
export function visibleNavGroups(
  groups: NavGroup[],
  viewer: {
    isAdmin: boolean;
    isPlatformAdmin?: boolean;
    showOriginalTimesheets: boolean;
    devOnly: boolean;
    features?: Record<string, boolean>;
  }
): NavGroup[] {
  return groups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => {
        if (item.adminOnly && !viewer.isAdmin) return false;
        if (item.platformAdmin && !viewer.isPlatformAdmin) return false;
        if (item.flag === 'originalTimesheets' && !viewer.showOriginalTimesheets) return false;
        if (item.flag === 'devOnly' && !viewer.devOnly) return false;
        if (item.feature && !viewer.features?.[item.feature]) return false;
        return true;
      })
    }))
    .filter((group) => group.items.length > 0);
}
